import { useMemo, useState } from 'react';

import { Link } from 'react-router-dom';

import { applyPathParams } from '@/api/fetcher';
import { Page } from '@/components/layout/Page';
import { CanvasRoutes, RouterConfig } from '@/configs/routes';
import { PAGE_PERSONA, POLICY_IDS } from '@/features/decisions/config/personaContext';
import { useActivePersonaContext } from '@/features/decisions/hooks/useActivePersonaContext';
import { useComparisonCandidates } from '@/features/decisions/hooks/useComparisonCandidates';
import { useHistoricalPerformance } from '@/features/decisions/hooks/useHistoricalPerformance';
import { useInventoryCoverage } from '@/features/decisions/hooks/useInventoryCoverage';
import { useTargetSLQuantiles } from '@/features/decisions/hooks/useTargetSLQuantiles';
import { forecastRunIdForPeriod } from '@/features/decisions/utils/forecastRunForPeriod';
import { policyLabel } from '@/features/decisions/utils/historicalPerformanceFormat';

import { ReorderEditor } from '../active-planning/ReorderEditor';
import { ActivePersonaContextSettings } from '../canvas/ActivePersonaContextSettings';
import { ScenarioControlsBar } from '../canvas/ScenarioControlsBar';
import { PoliciesConstraintsCashInputs } from '../policies/PoliciesConstraintsCashInputs';
import { PolicyDetailsPanel } from '../policies/PolicyDetailsPanel';
import { HistoricalPerformanceTable } from '../value-opportunity/HistoricalPerformanceTable';
import { InventoryCoverageChart } from '../value-opportunity/InventoryCoverageChart';
import { StockoutRiskChart } from '../value-opportunity/StockoutRiskChart';

const TABS = ['Active Recommendation', 'BackTest', 'Policies, Constraints, and Cash Inputs'] as const;
type Tab = (typeof TABS)[number];

export function ProductStockingContainer() {
  const {
    scenarioRunId,
    runId,
    sku: skuParam,
    site: siteParam,
    refExperimentId,
    experimentIdRaw: personaExperimentIdRaw,
    periodFrom,
    periodTo,
    setUrlParam,
    searchParams,
  } = useActivePersonaContext(PAGE_PERSONA.productStockingLocations);
  const sku = skuParam ?? PAGE_PERSONA.productStockingLocations.sku ?? '';
  const site = siteParam ?? PAGE_PERSONA.productStockingLocations.site ?? '';

  // This run's own top candidates replace PAGE_PERSONA's hardcoded default when experiment_id
  // hasn't been pinned explicitly — see useComparisonCandidates for why this isn't folded into
  // useActivePersonaContext itself.
  const { candidates, experimentIdRaw, experimentIds } = useComparisonCandidates({
    scenarioRunId,
    runId,
    experimentIdRaw: personaExperimentIdRaw,
    hasExplicitExperimentId: Boolean(searchParams.get('experiment_id')),
  });

  const rawTab = searchParams.get('tab');
  const tab: Tab = TABS.includes(rawTab as Tab) ? (rawTab as Tab) : TABS[0];

  const perf = useHistoricalPerformance({
    scenarioRunId,
    runId,
    sku,
    site,
    refExperimentId,
    experimentIds,
    periodFrom,
    periodTo,
  });

  // The dates the run actually produced, taken from the metric series rather than generated from
  // the window. The simulation steps on its own weekday — 2025-07-24 is a Thursday but the periods
  // land on Mondays — so stepping weekly from SIM_START offered six dates of which only SIM_END
  // had data, and the other five returned 404.
  const decisionDateOptions = useMemo<string[]>(() => {
    const periods = new Set<string>();
    for (const metric of perf.metrics) {
      for (const series of metric.series) {
        for (const point of series.period) periods.add(point.period);
      }
    }
    return [...periods].sort();
  }, [perf.metrics]);

  // The selected date is its own param. Writing it to period_to made each selection shrink the
  // window it was chosen from, so the list lost every date after the one picked.
  const requestedPeriod = searchParams.get('period');
  const latestPeriod = decisionDateOptions[decisionDateOptions.length - 1];

  // Empty until the periods are known, which leaves the coverage query disabled rather than
  // firing it at a date that probably has none. Falls back to the window's end if the metrics
  // never arrive, so a failed request does not leave the page with nothing to ask for.
  const effectivePeriod =
    requestedPeriod && decisionDateOptions.includes(requestedPeriod)
      ? requestedPeriod
      : (latestPeriod ?? (perf.isLoading ? '' : periodTo));

  const coverage = useInventoryCoverage({
    scenarioRunId,
    runId,
    sku,
    site,
    refExperimentId,
    experimentIds,
    period: effectivePeriod,
  });

  const forecastRunId = useMemo(() => forecastRunIdForPeriod(effectivePeriod), [effectivePeriod]);

  const spanCount = useMemo(() => {
    if (!coverage.planningHorizon) return 0;
    const ms =
      new Date(coverage.planningHorizon + 'T00:00:00Z').getTime() - new Date(effectivePeriod + 'T00:00:00Z').getTime();
    return Math.ceil(ms / (7 * 24 * 60 * 60 * 1000));
  }, [coverage.planningHorizon, effectivePeriod]);

  const leadtimeSpans = useMemo<Array<[number, number]>>(
    () => Array.from({ length: spanCount }, (_, i) => [0, (i + 1) * 7]),
    [spanCount],
  );

  const { spans, timestamps, mean, quantile80, quantile90, quantile95, grid } = useTargetSLQuantiles({
    runId: forecastRunId,
    fileId: 'aggregate/product_id_ship_to_region/forecast.parquet',
    primaryKeys: { product_id: sku, ship_to_region: site },
    leadtimeSpans,
    enabled: !coverage.isLoading && spanCount > 0,
  });

  const activeExperimentId = perf.header?.active_recommendation?.experiment_id ?? null;
  const comparisonPolicies = coverage.policies.filter((p) => !p.is_reference);
  const defaultPolicyId =
    comparisonPolicies.find((p) => p.experiment_id === POLICY_IDS.keystoneForecast)?.experiment_id ??
    comparisonPolicies[0]?.experiment_id ??
    refExperimentId;
  const selectedPolicyId = searchParams.get('policy') ?? defaultPolicyId;
  const selectedCoverage = coverage.policies.find((p) => p.experiment_id === selectedPolicyId);

  const [reorderOverride, setReorderOverride] = useState<number | null>(null);
  const overrideKey = `${selectedPolicyId}|${effectivePeriod}`;
  const [prevOverrideKey, setPrevOverrideKey] = useState(overrideKey);
  if (overrideKey !== prevOverrideKey) {
    setPrevOverrideKey(overrideKey);
    setReorderOverride(null);
  }

  const showRisk = spans.length > 0 && selectedPolicyId !== refExperimentId;
  const riskQuantiles = showRisk ? { timestamps, spans, mean, quantile80, quantile90, quantile95, grid } : undefined;

  const [chartView, setChartView] = useState<'coverage' | 'stockout'>('coverage');
  const activeView = chartView === 'stockout' && !showRisk ? 'coverage' : chartView;

  const heading = sku ? (
    <>
      <Link
        to={`/data/products?id=${encodeURIComponent(sku)}`}
        className="hover:underline"
        style={{ color: '#274483' }}
      >
        {sku}
      </Link>{' '}
      @ {site}
    </>
  ) : (
    `${sku} @ ${site}`
  );

  return (
    <Page
      title={heading}
      extraCrumbs={[
        { label: 'Backtest Results', href: RouterConfig[CanvasRoutes.DecisionsBacktests].path, clearSearch: true },
        {
          label: 'Backtest Scenario',
          href: applyPathParams(RouterConfig[CanvasRoutes.DecisionsBacktestDetail].path, { scenarioRunId }),
          clearSearch: true,
        },
        { label: 'Run Summary', href: RouterConfig[CanvasRoutes.DecisionsValueOpportunity].path },
        {
          label: 'Product / Stocking Locations',
          href: RouterConfig[CanvasRoutes.DecisionsProductStockingLocations].path,
        },
        { label: site },
        { label: sku },
      ]}
      actions={
        <ActivePersonaContextSettings
          scenarioRunId={scenarioRunId}
          runId={runId}
          sku={sku}
          site={site}
          refExperimentId={refExperimentId}
          experimentId={experimentIdRaw}
          experimentIds={experimentIds}
          candidates={candidates}
          periodFrom={periodFrom}
          periodTo={periodTo}
          onCommit={setUrlParam}
        />
      }
    >
      <div className="space-y-6">
        {/* Tabs */}
        <div className="flex gap-1 border-b border-gray-200">
          {TABS.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setUrlParam('tab', t)}
              className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
                t === tab ? 'border-[#274483] text-[#274483]' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === 'Active Recommendation' && (
          <>
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div>
                <ScenarioControlsBar
                  decisionDate={effectivePeriod}
                  decisionDateOptions={decisionDateOptions}
                  onChangeDecisionDate={(d) => setUrlParam('period', d)}
                  policySelector={
                    <ReorderEditor
                      policy={selectedCoverage}
                      isLoading={coverage.isLoading}
                      error={coverage.error}
                      overrideReorder={reorderOverride}
                      onOverrideChange={setReorderOverride}
                      reorderEditable={showRisk}
                    />
                  }
                />
              </div>
              <div>
                <div className="h-full rounded-lg border border-gray-200 bg-white p-4">
                  <PolicyDetailsPanel
                    policy={selectedCoverage}
                    policies={comparisonPolicies}
                    selectedExperimentId={selectedPolicyId}
                    activeExperimentId={activeExperimentId}
                    onSelect={(id) => setUrlParam('policy', id)}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-0">
              <div className="flex gap-1 rounded-lg border border-gray-200 bg-gray-50 p-1 text-sm">
                {(
                  [
                    { id: 'coverage', label: 'Inventory Coverage vs Demand', enabled: true },
                    { id: 'stockout', label: 'Weekly Stockout Risk', enabled: showRisk },
                  ] as const
                ).map((v) => (
                  <button
                    key={v.id}
                    type="button"
                    disabled={!v.enabled}
                    onClick={() => setChartView(v.id)}
                    className={`flex-1 rounded-md px-3 py-1.5 font-medium transition-colors ${
                      activeView === v.id
                        ? 'bg-white text-[#274483] shadow-sm'
                        : v.enabled
                          ? 'text-gray-600 hover:text-gray-900'
                          : 'cursor-not-allowed text-gray-300'
                    }`}
                  >
                    {v.label}
                  </button>
                ))}
              </div>

              {activeView === 'coverage' ? (
                <InventoryCoverageChart
                  policy={selectedCoverage}
                  decisionDate={effectivePeriod}
                  planningHorizon={coverage.planningHorizon}
                  isLoading={coverage.isLoading}
                  error={coverage.error}
                  quantiles={riskQuantiles}
                />
              ) : (
                <StockoutRiskChart
                  policy={selectedCoverage}
                  policyLabel={selectedCoverage ? policyLabel(selectedCoverage) : '—'}
                  decisionDate={effectivePeriod}
                  quantiles={riskQuantiles}
                  overrideReorder={reorderOverride}
                  isLoading={coverage.isLoading}
                  error={coverage.error}
                />
              )}
            </div>
          </>
        )}

        {tab === 'Policies, Constraints, and Cash Inputs' && <PoliciesConstraintsCashInputs />}

        {tab === 'BackTest' && (
          <HistoricalPerformanceTable
            metrics={perf.metrics}
            refExperimentId={refExperimentId}
            experimentIds={experimentIds}
            optimizedExperimentId={perf.header?.fcf_opportunity?.experiment_id ?? null}
            netFcfUnlock={perf.header?.fcf_opportunity?.model_fcf_delta ?? null}
            periodFrom={perf.resolved?.period_from ?? periodFrom}
            periodTo={perf.resolved?.period_to ?? periodTo}
            periodCount={perf.resolved?.period_count ?? 0}
            isLoading={perf.isLoading}
            error={perf.error}
          />
        )}
      </div>
    </Page>
  );
}
